import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box, Button, Flex,
  FormControl, FormErrorMessage,
  HStack,
  Input,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Tab,
  Table,
  TableContainer,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
} from "@chakra-ui/react";
import dayjs from "dayjs";
import React, { memo, useEffect, useMemo, useState } from "react";
import { codeToNameMap, NonWorkingItems, NonWorkingItemsDisplayOrder, ShiftSystem, } from "../../contexts/constants";
import { Errors, NonWorkingHoursType, NonWorkingType } from "../../contexts/types";
import { getFiscalYearsString, isSameFiscalYear } from "../../utils/calculation";
import { useTranslation } from 'react-i18next';
import { hourMinutesStringToMinutes, minutesToHourMinutesString } from "../../utils/utils";
import '../common.css';
import CeasTimePicker from "./CeasTimePicker";
import HelpIcon from "./HelpIcon";

/**
 * パラメータ
 */
interface Props {
  isSimulation: boolean,
  nonWorkingHoursData: { fiscalYear: string, nonWorkingHoursByMonth: NonWorkingHoursType[] }[],
  setNonWorkingHoursData: React.Dispatch<React.SetStateAction<{ fiscalYear: string, nonWorkingHoursByMonth: NonWorkingHoursType[] }[]>>,
  projectStartDate: string,
  /** 値変更時のコールバック（エラーメッセージクリア用） */
  onValueChange?: (fiscalYear: string, monthIndex: number, fieldType: 'workingHours' | 'nonWorkingHours' | 'other') => void,
  maxHeight: string,
}

/**
 * 非稼働時間
 * @param props パラメータ
 * @returns
 */
const NonWorkingHours: React.FC<Props> = memo((props: Props) => {
  const { t } = useTranslation();
  /** 幅 - 項目名 */
  const LABEL_WIDTH = "110px";

  // console.info(`★レンダリング - 非稼働時間`); // 再描画確認用

  /** タイトル */
  const TITLE = t('features.InvestmentScreening.Scheduling.field.nonWorkingHours');

  /** 翻訳キープレフィックス */
  const NWH = 'features.InvestmentScreening.NonWorkingHours';

  /** デフォルト値 */
  const DEFAULT_TIME = "00:00";

  /** 一日あたりの時間(２４ｈ) */
  const HOURS_PER_DAY = 24;

  /** トースト表示用 */
  const toast = useToast();

  /**
   * エラー情報をクリアする。
   * @returns デフォルトエラー情報
   */
  const clearErrors = (): Errors => {
    return {
      workTime: {
        isError: false,
        errorMessage: "",
      },
      overTime: {
        isError: false,
        errorMessage: "",
      },
      shiftSystem: {
        isError: false,
        errorMessage: "",
      },
      nonWorkingItems: {
        isError: false,
        errorMessage: "",
      },
    };
  }

  /** バリデーションエラー情報 */
  const [errors, setErrors] = useState<Errors>(clearErrors());

  /** 【一括入力】 開始年月 */
  const [startYearMonth, setStartYearMonth] = useState({ year: "", month: 0 });

  /** 【一括入力】 終了年月 */
  const [endYearMonth, setEndYearMonth] = useState({ year: "", month: 0 });

  /** 【一括入力】 非稼働時間情報 */
  const [rangeInput, setRangeInput] = useState<NonWorkingHoursType>({
    workTime: DEFAULT_TIME,
    overTime: DEFAULT_TIME,
    shiftSystem: ShiftSystem.OneShiftSystem.code,
    nonWorkingItems: Object.entries(codeToNameMap(NonWorkingItems))
      .map(([code]) => ({
        nonWorkingId: code,
        nonWorkingHour: DEFAULT_TIME,
      })),
    operatingHour: DEFAULT_TIME,
    nonWorkingHour: DEFAULT_TIME,
    workingHours: DEFAULT_TIME,
    availabilityRate: 1,
    unavailabilityRate: 0,
  });

  /** 【一括入力】 エラー情報 */
  const [errorsRangeInput, setErrorsRangeInput] = useState<Errors>(clearErrors());

  /**
   * 年度配列を生成
   */
  const fiscalYearsString = useMemo(() => {
    const totalFiscalYears = props.nonWorkingHoursData.length;
    return getFiscalYearsString(props.projectStartDate || dayjs().format("YYYY-MM-DD"), totalFiscalYears);
  }, [props.projectStartDate, props.nonWorkingHoursData.length]);

  /**
   * 一括入力の開始年月および終了年月の初期化
   */
  useEffect(() => {

    if (props?.nonWorkingHoursData?.length > 0) {

      const firstYearData = props.nonWorkingHoursData[0];
      const lastYearData = props.nonWorkingHoursData[props.nonWorkingHoursData.length - 1];

      // 開始年月
      setStartYearMonth({
        year: firstYearData.fiscalYear,
        month: 4,
      });

      // 終了年月
      setEndYearMonth({
        year: lastYearData.fiscalYear,
        month: 3,
      });

    }

  }, []); // props更新を監視

  /**
   * 画面表示時に非稼働項目の補填と勤務体制の初期値を設定
   */
  useEffect(() => {
    // 初期値生成関数
    const createInitialNonWorkingHoursData = (): NonWorkingHoursType[] => {
      const nonWorkingItemsMap: [string, string][] = Object.entries(codeToNameMap(NonWorkingItems));

      return Array(12).fill(null).map(() => ({
        workTime: DEFAULT_TIME,
        overTime: DEFAULT_TIME,
        shiftSystem: ShiftSystem.OneShiftSystem.code,
        nonWorkingItems: nonWorkingItemsMap.map(([code]) => ({
          nonWorkingId: code,
          nonWorkingHour: DEFAULT_TIME,
        })),
        operatingHour: DEFAULT_TIME,
        nonWorkingHour: DEFAULT_TIME,
        workingHours: DEFAULT_TIME,
        availabilityRate: 1,
        unavailabilityRate: 0,
      }));
    };

    // 正しい年度配列を生成
    const correctFiscalYears = getFiscalYearsString(
      props.projectStartDate || dayjs().format("YYYY-MM-DD"),
      props.nonWorkingHoursData.length
    );

    const nonWorkingItemsMap: [string, string][] = Object.entries(codeToNameMap(NonWorkingItems));

    // 各正しい年度について、DBデータとマッチング
    const updatedNonWorkingHours = correctFiscalYears.map((correctFiscalYear, yearIndex) => {
      // DBデータから一致する年度を検索（最初にマッチしたものを使用）
      // ※会計年度文字列は表示言語でフォーマットが変わる（例：日本語「2025年3月期」／英語「FY2025」）ため、
      // 完全一致ではなく年数値で判定する（isSameFiscalYear）。これにより、保存時と異なる言語で開いても
      // 既存データが「該当年度なし」と誤判定されて初期値にリセットされることを防ぐ
      const matchedData = props.nonWorkingHoursData.find(item => isSameFiscalYear(item.fiscalYear, correctFiscalYear));

      let nonWorkingHoursByMonth: NonWorkingHoursType[];

      if (matchedData) {
        // 一致する場合：DBデータを使用（補完処理あり）
        nonWorkingHoursByMonth = matchedData.nonWorkingHoursByMonth.map((monthData, month) => {
          // 非稼働項目を初期化
          if (!monthData.nonWorkingItems) {
            monthData.nonWorkingItems = [];
          }

          // 非稼働項目を補完
          nonWorkingItemsMap.forEach(([code, _]) => {
            if (!monthData.nonWorkingItems?.some((n) => n.nonWorkingId === code)) {
              monthData.nonWorkingItems?.push({
                nonWorkingId: code,
                nonWorkingHour: DEFAULT_TIME,
              });
            }
          });

          // 勤務体制を設定
          const shiftSystem = monthData.shiftSystem || ShiftSystem.OneShiftSystem.code;

          // 総労働時間と残業時間の補完
          const workTime = monthData.workTime || DEFAULT_TIME;
          const overTime = monthData.overTime || DEFAULT_TIME;

          // 非稼働時間の計算
          const {
            operatingHour,
            nonWorkingHour,
            availabilityRate,
            unavailabilityRate,
          } = calcNonWorkingHours(monthData.nonWorkingItems);

          // 勤務時間計算
          handleCalcWorkingHour(correctFiscalYear, month, workTime, overTime, shiftSystem, true);

          return {
            ...monthData,
            workTime,
            overTime,
            shiftSystem,
            operatingHour,
            nonWorkingHour,
            availabilityRate,
            unavailabilityRate,
          } as NonWorkingHoursType;
        });
      } else {
        // 一致しない場合：初期値を使用
        nonWorkingHoursByMonth = createInitialNonWorkingHoursData();
      }

      return {
        fiscalYear: correctFiscalYear,
        nonWorkingHoursByMonth: nonWorkingHoursByMonth,
      };
    });

    // 更新後のデータを反映
    props.setNonWorkingHoursData(updatedNonWorkingHours);

  }, []);

  /**
   * 「労働時間」を算出する。
   * @param workTime 標準労働時間（hh:mm）
   * @param overTime 残業時間（hh:mm）
   * @param shiftSystem 勤務体制値
   * @param enabledOverTime 残業時間有無（true：有効、false：無効）
   * @returns 労働時間
   */
  const calcWorkingHoursMinutes = (workTime: string | undefined, overTime: string | undefined, shiftSystem: string | undefined, enabledOverTime: boolean): number => {
    // 勤務体制
    const shiftSystemValue = parseInt(shiftSystem || "0");

    // 労働時間
    const operatingTime = (
      hourMinutesStringToMinutes(workTime) + (enabledOverTime ? hourMinutesStringToMinutes(overTime) : 0)
    ) * shiftSystemValue;

    // 返却
    return operatingTime;
  }

  /**
   * 「非稼働項目の合計時間」を分で返す。
   * @param nonWorkingItems 非稼働項目
   */
  const calcSumNonWorkingItems = (nonWorkingItems: NonWorkingType[] | undefined): number => {
    if (!nonWorkingItems) {
      nonWorkingItems = [];
    }

    let sumNonWorkingItems = 0;
    nonWorkingItems.map((item) => {
      // 分換算して加算
      sumNonWorkingItems += hourMinutesStringToMinutes(item.nonWorkingHour);
    });
    return sumNonWorkingItems;
  }

  /**
   * 稼働時間、稼働率、未割当時間、非稼働時間、非稼働率を算出する。
   * @param shiftSystem 勤務体制
   */
  const calcNonWorkingHours = (nonWorkingItems?: NonWorkingType[]) => {
    // 24hの分換算
    const hoursPerDayMinutes = HOURS_PER_DAY * 60;

    // 非稼働時間 = 非稼働時間の合計時間（算出）
    const nonWorkingMinutes = calcSumNonWorkingItems(nonWorkingItems);

    // 非稼働時間（Minutes ⇒ Hour換算）
    const nonWorkingHour = minutesToHourMinutesString(nonWorkingMinutes);

    // 稼働時間 = 24h - 非稼働時間の合計時間
    const operatingMinutes = hoursPerDayMinutes - nonWorkingMinutes;

    // 稼働時間（Minutes ⇒ Hour換算）
    const operatingHour = minutesToHourMinutesString(operatingMinutes);

    // 稼働率
    const availabilityRate = Number((operatingMinutes / hoursPerDayMinutes).toFixed(3));

    // 非稼働率
    const unavailabilityRate = Number((nonWorkingMinutes / hoursPerDayMinutes).toFixed(3));

    return {
      operatingHour: operatingHour,
      nonWorkingHour: nonWorkingHour,
      availabilityRate: availabilityRate,
      unavailabilityRate: unavailabilityRate,
    };
  }

  /**
   * 稼働時間、稼働率、非稼働時間、非稼働率を算出する。
   * @param fiscalYear 年度文字列
   * @param monthIndex 月インデックス（0から始まる配列のインデックス）
   * @param nonWorkingItems 非稼働項目データ
   */
  const handleCalcBasicHour = (fiscalYear: string, monthIndex: number, nonWorkingItems: NonWorkingType[]) => {
    // 稼働時間、稼働率、非稼働時間、非稼働率を算出
    const { operatingHour, nonWorkingHour, availabilityRate, unavailabilityRate } = calcNonWorkingHours(nonWorkingItems);

    // 反映
    props.setNonWorkingHoursData(prevState => {
      if (!prevState) return prevState;

      // fiscalYear文字列で対象データを検索
      const targetIndex = prevState.findIndex(item => item.fiscalYear === fiscalYear);
      if (targetIndex < 0) {
        return prevState;
      }

      const updatedState = [...prevState];
      const updatedMonths = updatedState[targetIndex].nonWorkingHoursByMonth.map((monthData, idx) => {
        if (idx === monthIndex) {
          return {
            ...monthData,
            operatingHour: operatingHour,
            nonWorkingHour: nonWorkingHour,
            availabilityRate: availabilityRate,
            unavailabilityRate: unavailabilityRate,
          };
        }
        return monthData;
      });

      updatedState[targetIndex] = {
        ...updatedState[targetIndex],
        nonWorkingHoursByMonth: updatedMonths,
      };

      return updatedState;
    });
  };

  /**
   * 労働時間を算出する。
   * @param fiscalYear 年度文字列
   * @param monthIndex 月インデックス（0から始まる配列のインデックス）
   * @param workTime 標準稼働時間
   * @param overTime 残業時間
   * @param shiftSystem 勤務体制
   * @param enabledOverTime 残業時間有無（true：有効、false：無効）
   */
  const handleCalcWorkingHour = (
    fiscalYear: string,
    monthIndex: number,
    workTime: string | undefined,
    overTime: string | undefined,
    shiftSystem: string | undefined,
    enabledOverTime: boolean
  ) => {
    // 勤務体制
    const shiftSystemValue = parseInt(shiftSystem || "0");

    // 労働時間を算出
    const workingHourMinutes = (
      hourMinutesStringToMinutes(workTime) + (enabledOverTime ? hourMinutesStringToMinutes(overTime) : 0)
    ) * shiftSystemValue;

    // 労働時間（Minutes ⇒ Hours換算）
    const workingHours = minutesToHourMinutesString(workingHourMinutes);

    // 反映
    changeNonWorkingHoursData(fiscalYear, monthIndex, "workingHours", workingHours);
  };

  /**
   * 指定したプロパティ名の値を変更する。
   * @param fiscalYear 年度文字列
   * @param monthIndex 月インデックス（0から始まる配列のインデックス）
   * @param propertyName プロパティ名
   * @param value 値
   */
  const changeNonWorkingHoursData = (
    fiscalYear: string,
    monthIndex: number,
    propertyName: 'workTime' | 'overTime' | 'shiftSystem' | 'workingHours' | 'operatingHour' | 'nonWorkingHour' | 'availabilityRate' | 'unavailabilityRate',
    value: NonWorkingHoursType[typeof propertyName]) => {

    props.setNonWorkingHoursData(prevState => {
      if (!prevState) return prevState;

      // fiscalYear文字列で対象データを検索
      const targetIndex = prevState.findIndex(item => item.fiscalYear === fiscalYear);
      if (targetIndex < 0) {
        return prevState;
      }

      const updatedState = [...prevState];
      const updatedMonths = updatedState[targetIndex].nonWorkingHoursByMonth.map((monthData, idx) => {
        if (idx === monthIndex) {
          // 該当月のプロパティを更新する
          return {
            ...monthData,
            [propertyName]: value,
          };
        }
        return monthData; // 対象月でない場合はそのまま返す
      });

      updatedState[targetIndex] = {
        ...updatedState[targetIndex],
        nonWorkingHoursByMonth: updatedMonths,
      };

      return updatedState;
    });

    // エラーメッセージクリア用コールバックを呼び出す
    // workTime, overTime, shiftSystem, workingHoursは労働時間関連
    const fieldType: 'workingHours' | 'nonWorkingHours' | 'other' =
      ['workTime', 'overTime', 'shiftSystem', 'workingHours'].includes(propertyName) ? 'workingHours' : 'other';
    props.onValueChange?.(fiscalYear, monthIndex, fieldType);
  };

  /**
   * 非稼働項目を正規の項目一覧から再構築する。
   *
   * 非稼働項目は配列で保存されるため、項目が追加される前に保存されたデータには
   * 新しい項目（休憩時間・計画停止など）が存在しない。既存配列の書き換えだけでは
   * そうした項目に値を設定できず「入力しても変更されない」状態になるため、
   * 常に正規の項目一覧から再構築し、存在しない項目は既定値で補完する。
   *
   * @param items 現在の非稼働項目（undefined可）
   * @param override 値を上書きする項目（省略時は補完のみ）
   * @returns 全項目を含む非稼働項目の配列
   */
  const buildNonWorkingItems = (
    items: NonWorkingType[] | undefined,
    override?: { id: string; value: string }
  ): NonWorkingType[] => {
    const existing = items ?? [];
    return Object.keys(codeToNameMap(NonWorkingItems)).map((code) => {
      const found = existing.find((item) => item.nonWorkingId === code);
      return {
        nonWorkingId: code,
        nonWorkingHour:
          override && override.id === code
            ? override.value
            : (found?.nonWorkingHour ?? DEFAULT_TIME),
      };
    });
  };

  /**
   * 非稼働項目の選択値を変更する。
   * @param fiscalYear 年度文字列
   * @param monthIndex 月インデックス（0から始まる配列のインデックス）
   * @param id 非稼働項目ID
   * @param value 値
   */
  const changeNonWorkingItems = (fiscalYear: string, monthIndex: number, id: string, value: string) => {
    props.setNonWorkingHoursData((prevState) => {
      if (!prevState) return prevState;

      // fiscalYear文字列で対象データを検索
      const targetIndex = prevState.findIndex(item => item.fiscalYear === fiscalYear);
      if (targetIndex < 0) {
        return prevState;
      }

      const updatedState = [...prevState];
      const updatedMonths = updatedState[targetIndex].nonWorkingHoursByMonth.map((monthData, idx) => {
        if (idx === monthIndex) {
          // 月のインデックスが一致するデータを更新
          // （項目が欠けている既存データでも値を設定できるよう、正規の項目一覧から再構築する）
          const updatedItems = buildNonWorkingItems(monthData.nonWorkingItems, { id, value });

          return {
            ...monthData,
            nonWorkingItems: updatedItems,
          };
        }
        return monthData; // 対象月でない場合は元データをそのまま返す
      });

      updatedState[targetIndex] = {
        ...updatedState[targetIndex],
        nonWorkingHoursByMonth: updatedMonths,
      };

      return updatedState;
    });

    // エラーメッセージクリア用コールバックを呼び出す（非稼働項目の変更）
    props.onValueChange?.(fiscalYear, monthIndex, 'nonWorkingHours');
  };

  /**
   * エラー情報を変更する。
   * @param propertyName プロパティ名
   * @param isError true：入力エラー、false：入力正常
   * @param errorMessage エラーメッセージ
   * @param rangeInputFlag true：一括入力、false：デフォルト値
   */
  const changeErrors = (propertyName: string, isError: boolean, errorMessage: string, rangeInputFlag: boolean = false) => {
    if (rangeInputFlag) {
      setErrorsRangeInput((prevState => ({
        ...prevState,
        [propertyName]: {
          isError: isError,
          errorMessage: errorMessage,
        },
      })));
    } else {
      setErrors((prevState => ({
        ...prevState,
        [propertyName]: {
          isError: isError,
          errorMessage: errorMessage,
        },
      })));
    }
  }

  /**
   * 入力項目の入力チェックを行う。
   * @param workTime 標準労働時間
   * @param overTime 残業時間
   * @param shiftSystem 勤務体制
   * @param nonWorkingItems 非稼働項目
   * @param rangeInputFlag true：一括入力、false：デフォルト値
   * @returns true：正常、false：異常
   */
  const validOperatingHour = (
    workTime: string | undefined,
    overTime: string | undefined,
    shiftSystem: string | undefined,
    rangeInputFlag: boolean = false
  ): boolean => {

    if (rangeInputFlag) {
      setErrorsRangeInput(prevState => ({
        ...prevState,
        worktime: { isError: false, errorMessage: `` },
        overTime: { isError: false, errorMessage: `` },
        shiftSystem: { isError: false, errorMessage: `` },
      }));
    } else {
      setErrors(prevState => ({
        ...prevState,
        worktime: { isError: false, errorMessage: `` },
        overTime: { isError: false, errorMessage: `` },
        shiftSystem: { isError: false, errorMessage: `` },
      }));
    }

    // 24hの分換算
    const hoursPerDayMinutes = HOURS_PER_DAY * 60;

    // 労働時間（分）
    const workingHoursMinutes = calcWorkingHoursMinutes(workTime, overTime, shiftSystem, true);

    if (rangeInputFlag) {
      // 労働時間が２４ｈ超過判定
      if (hoursPerDayMinutes < workingHoursMinutes) {
        changeErrors("workTime", true, t(`${NWH}.validation.workTimeExceeds`, { time: minutesToHourMinutesString(workingHoursMinutes), hours: HOURS_PER_DAY }), true);
        changeErrors("overTime", true, ``, true);
        changeErrors("shiftSystem", true, ``, true);
        toast({
          title: TITLE,
          description: t(`${NWH}.toast.inputError`),
          status: 'warning',
          duration: 2000,
          isClosable: true,
        });
        return false;
      }
    } else {
      // 労働時間が２４ｈ超過判定
      if (hoursPerDayMinutes < workingHoursMinutes) {
        changeErrors("workTime", true, t(`${NWH}.validation.workTimeExceeds`, { time: minutesToHourMinutesString(workingHoursMinutes), hours: HOURS_PER_DAY }));
        changeErrors("overTime", true, ``);
        changeErrors("shiftSystem", true, ``);
        toast({
          title: TITLE,
          description: t(`${NWH}.toast.inputError`),
          status: 'warning',
          duration: 2000,
          isClosable: true,
        });
        return false;
      }
    }

    return true;
  }

  /**
   * 入力項目の入力チェックを行う。
   * @param nonWorkingItems 非稼働項目
   * @param rangeInputFlag true：一括入力、false：デフォルト値
   * @returns true：正常、false：異常
   */
  const validNonWorkingHours = (nonWorkingItems: NonWorkingType[] | undefined, rangeInputFlag: boolean = false): boolean => {

    if (rangeInputFlag) {
      setErrorsRangeInput(prevState => ({
        ...prevState,
        nonWorkingItems: { isError: false, errorMessage: `` },
      }));
    } else {
      setErrors(prevState => ({
        ...prevState,
        nonWorkingItems: { isError: false, errorMessage: `` },
      }));
    }

    // 24hの分換算
    const hoursPerDayMinutes = HOURS_PER_DAY * 60;

    // 非稼働項目の合計時間（分）
    const sumNonWorkingItems = calcSumNonWorkingItems(nonWorkingItems);

    if (rangeInputFlag) {
      if (hoursPerDayMinutes < sumNonWorkingItems) {
        // 非稼働項目の合計時間が24hを超過した場合はエラー
        changeErrors("nonWorkingItems", true, t(`${NWH}.validation.nonWorkingTimeExceeds`, { time: minutesToHourMinutesString(sumNonWorkingItems), hours: HOURS_PER_DAY }), true);

        toast({
          title: TITLE,
          description: t(`${NWH}.toast.inputError`),
          status: 'warning',
          duration: 2000,
          isClosable: true,
        });
        return false;
      }
    } else {
      if (hoursPerDayMinutes < sumNonWorkingItems) {
        // 非稼働項目の合計時間が24hを超過した場合はエラー
        changeErrors("nonWorkingItems", true, t(`${NWH}.validation.nonWorkingTimeExceeds`, { time: minutesToHourMinutesString(sumNonWorkingItems), hours: HOURS_PER_DAY }));

        toast({
          title: TITLE,
          description: t(`${NWH}.toast.inputError`),
          status: 'warning',
          duration: 2000,
          isClosable: true,
        });
        return false;
      }
    }

    return true;
  }

  /** 非稼働項目一覧（画面表示順、コード・名称のペア） */
  const nonWorkingItemsOrdered: [string, string][] = NonWorkingItemsDisplayOrder.map(
    (code) => [code, codeToNameMap(NonWorkingItems)[code]]
  );

  /** 非稼働項目テーブルの2カラム分割（縦に長くなりすぎないよう2列表示にする） */
  const nonWorkingItemsHalf = Math.ceil(nonWorkingItemsOrdered.length / 2);
  const nonWorkingItemsColumns: [string, string][][] = [
    nonWorkingItemsOrdered.slice(0, nonWorkingItemsHalf),
    nonWorkingItemsOrdered.slice(nonWorkingItemsHalf),
  ];

  /**
   * 非稼働項目テーブル（1カラム分）を描画する。
   * @param items 非稼働項目（コード・名称のペア）配列
   * @param getValue 非稼働時間の取得関数
   * @param onChangeItem 非稼働時間変更時のハンドラ
   * @param isInvalid 入力エラー有無
   * @param onBlurItem フォーカスアウト時のハンドラ
   * @returns テーブル要素
   */
  const renderNonWorkingItemsColumn = (
    items: [string, string][],
    getValue: (code: string) => string,
    onChangeItem: (code: string, value: string) => void,
    isInvalid: boolean,
    onBlurItem: () => void,
  ) => (
    <TableContainer overflowX="hidden">
      <Table size="sm">
        <Thead position="sticky" top={0} backgroundColor="white" zIndex={100}>
          <Tr>
            <Th>{t(`${NWH}.nonWorkingItemName`)}</Th>
            <Th>{t(`${NWH}.timeHeader`)}</Th>
          </Tr>
        </Thead>
        <Tbody>
          {items.map(([code, name]) => (
            <Tr key={code}>
              <Td padding={1}>
                <Flex align="center" gap={1}>
                  <Text whiteSpace="nowrap">{name}</Text>
                  {code === NonWorkingItems.Setup.code && (
                    <HelpIcon size="20px">
                      <Text whiteSpace="pre-line">{t(`${NWH}.help.setupNote`)}</Text>
                    </HelpIcon>
                  )}
                  {code === NonWorkingItems.PlannedStoppage.code && (
                    <HelpIcon size="20px">
                      <Text whiteSpace="pre-line">{t(`${NWH}.help.plannedStoppageNote`)}</Text>
                    </HelpIcon>
                  )}
                </Flex>
              </Td>
              <Td padding={1}>
                <CeasTimePicker
                  isInvalid={isInvalid}
                  value={getValue(code)}
                  onChange={(value) => onChangeItem(code, value)}
                  onBlur={onBlurItem}
                />
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </TableContainer>
  );

  /**
   * レイアウト
   */
  return (
    <>

      <Box className="simulation-working-days-container" maxHeight={props.maxHeight} overflowY="auto">

        <Stack spacing={0}>

          {/* 一括入力アコーディオン */}
          <Accordion allowToggle defaultIndex={[0]}>
            <AccordionItem>
              <h2>
                <AccordionButton>
                  <Box flex="1" textAlign="left">
                    {t(`${NWH}.bulkInput`)}
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h2>
              <AccordionPanel pb={4}>
                <Stack spacing={4}>

                  <HStack spacing={4} align="start">

                    {/* 開始年月 */}
                    <FormControl w={"fit-content"}>
                      <Flex align="center">
                        <Text minW={95} m={0} mr={2} whiteSpace="nowrap">{t(`${NWH}.startMonth`)}</Text>
                        <Select
                          value={`${startYearMonth.year}-${startYearMonth.month}`}
                          onChange={(e) => {
                            const [year, month] = e.target.value.split("-");
                            setStartYearMonth({ year, month: Number(month) });
                          }}
                        >
                          {props?.nonWorkingHoursData?.flatMap((yearData, yearIndex) =>
                            Array.from({ length: 12 }, (_, idx) => {
                              const month = idx < 9 ? idx + 4 : idx - 8;
                              return {
                                value: `${fiscalYearsString[yearIndex]}-${month}`,
                                display: `${fiscalYearsString[yearIndex]} ${t('common.format.month', { month })}`,
                              };
                            })
                          ).map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.display}
                            </option>
                          ))}
                        </Select>
                      </Flex>
                    </FormControl>

                    {/* 終了年月 */}
                    <FormControl ml={2} w={"fit-content"}>
                      <Flex align="center">
                        <Text minW={95} m={0} mr={2} whiteSpace="nowrap">{t(`${NWH}.endMonth`)}</Text>
                        <Select
                          value={`${endYearMonth.year}-${endYearMonth.month}`}
                          onChange={(e) => {
                            const [year, month] = e.target.value.split("-");
                            setEndYearMonth({ year, month: Number(month) });
                          }}
                        >
                          {props?.nonWorkingHoursData?.flatMap((yearData, yearIndex) =>
                            Array.from({ length: 12 }, (_, idx) => {
                              const month = idx < 9 ? idx + 4 : idx - 8;
                              return {
                                value: `${fiscalYearsString[yearIndex]}-${month}`,
                                display: `${fiscalYearsString[yearIndex]} ${t('common.format.month', { month })}`,
                              };
                            })
                          ).map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.display}
                            </option>
                          ))}
                        </Select>
                      </Flex>
                    </FormControl>

                  </HStack>

                  <HStack spacing={0} align="start" minH={12}>

                    {/* 標準労働時間 */}
                    <FormControl w="fit-content" minW="220px" isInvalid={errorsRangeInput.workTime.isError}>
                      <Flex align="center">
                        <Text minW={LABEL_WIDTH} m={0} mr={2} whiteSpace="nowrap">{t(`${NWH}.standardWorkTime`)}</Text>
                        <CeasTimePicker
                          isInvalid={errorsRangeInput.workTime.isError}
                          value={rangeInput.workTime || DEFAULT_TIME}
                          onChange={(value) => {

                            // 入力チェック
                            if (!validOperatingHour(value, rangeInput.overTime, rangeInput.shiftSystem, true)) return;

                            // 労働時間を算出
                            const shiftSystemValue = parseInt(rangeInput.shiftSystem || "0");
                            const workingHourMinutes = (
                              hourMinutesStringToMinutes(value) + hourMinutesStringToMinutes(rangeInput.overTime)
                            ) * shiftSystemValue;

                            // 分から時間に変換
                            const workingHours = minutesToHourMinutesString(workingHourMinutes);

                            // state更新
                            setRangeInput((prev) => ({ ...prev, workTime: value, workingHours: workingHours }));

                          }}
                          onBlur={() => {
                            setErrorsRangeInput(clearErrors());
                          }}
                        />
                      </Flex>
                      <FormErrorMessage>{errorsRangeInput.workTime.errorMessage}</FormErrorMessage>
                    </FormControl>

                    {/* 残業時間 */}
                    <FormControl ml={6} w="185px" isInvalid={errorsRangeInput.overTime.isError}>
                      <Flex align="center">
                        <Text w={75} m={0} whiteSpace="nowrap">{t(`${NWH}.overTime`)}</Text>
                        <CeasTimePicker
                          isInvalid={errorsRangeInput.overTime.isError}
                          value={rangeInput.overTime || DEFAULT_TIME}
                          onChange={(value) => {

                            // 入力チェック
                            if (!validOperatingHour(rangeInput.workTime, value, rangeInput.shiftSystem, true)) return;

                            // 労働時間を算出
                            const shiftSystemValue = parseInt(rangeInput.shiftSystem || "0");
                            const workingHourMinutes = (
                              hourMinutesStringToMinutes(rangeInput.workTime) + hourMinutesStringToMinutes(value)
                            ) * shiftSystemValue;

                            // 分から時間に変換
                            const workingHours = minutesToHourMinutesString(workingHourMinutes);

                            // state更新
                            setRangeInput((prev) => ({ ...prev, overTime: value, workingHours: workingHours }));

                          }}
                          onBlur={() => {
                            setErrorsRangeInput(clearErrors());
                          }}
                        />
                      </Flex>
                      <FormErrorMessage>{errorsRangeInput.overTime.errorMessage}</FormErrorMessage>
                    </FormControl>

                    {/* 勤務体制 */}
                    <FormControl ml={6} w="220px" isInvalid={errorsRangeInput.shiftSystem.isError}>
                      <Flex align="center">
                        <Text minW={90} m={0} mr={2} whiteSpace="nowrap">{t(`${NWH}.shiftSystem`)}</Text>
                        <Select w="130px"
                          value={rangeInput.shiftSystem}
                          onChange={(e) => {

                            // 入力チェック
                            if (!validOperatingHour(rangeInput.workTime, rangeInput.overTime, e.target.value, true)) return;

                            // 労働時間を算出
                            const shiftSystemValue = parseInt(e.target.value || "0");
                            const workingHourMinutes = (
                              hourMinutesStringToMinutes(rangeInput.workTime) + hourMinutesStringToMinutes(rangeInput.overTime)
                            ) * shiftSystemValue;

                            // 分から時間に変換
                            const workingHours = minutesToHourMinutesString(workingHourMinutes);

                            // state更新
                            setRangeInput((prev) => ({ ...prev, shiftSystem: e.target.value, workingHours: workingHours }));

                          }}
                          onBlur={() => {
                            setErrorsRangeInput(clearErrors());
                          }}
                        >
                          {Object.entries(codeToNameMap(ShiftSystem)).map(([code, name]) => (
                            <option key={code} value={code}>
                              {name}
                            </option>
                          ))}
                        </Select>
                      </Flex>
                      <FormErrorMessage>{errorsRangeInput.shiftSystem.errorMessage}</FormErrorMessage>
                    </FormControl>

                  </HStack>

                  {/* 非稼働項目 */}
                  <FormControl isInvalid={errorsRangeInput.nonWorkingItems.isError}>
                    <Flex align="start">
                      <Text minW={LABEL_WIDTH} m={0} mt={3} whiteSpace="nowrap">
                        {t(`${NWH}.nonWorkingItems`)}
                      </Text>
                      <SimpleGrid columns={2} spacing={4} flex={1}>
                        {nonWorkingItemsColumns.map((columnItems, columnIndex) => (
                          <Box key={columnIndex}>
                            {renderNonWorkingItemsColumn(
                              columnItems,
                              (code) => rangeInput.nonWorkingItems?.find(
                                (item) => item.nonWorkingId === code
                              )?.nonWorkingHour || DEFAULT_TIME,
                              (code, value) => {

                                // JSONパース
                                const nonWorkingItems: NonWorkingType[] = JSON.parse(
                                  JSON.stringify(rangeInput.nonWorkingItems)
                                );

                                // 現在の入力値を仮反映
                                nonWorkingItems?.forEach((item) => {
                                  if (item.nonWorkingId === code) {
                                    item.nonWorkingHour = value;
                                  }
                                });

                                // 入力チェック
                                if (!validNonWorkingHours(nonWorkingItems, true)) {
                                  return;
                                }

                                // 稼働時間、稼働率、非稼働時間、非稼働率を算出
                                const { operatingHour, nonWorkingHour, availabilityRate, unavailabilityRate }
                                  = calcNonWorkingHours(nonWorkingItems);

                                // state更新
                                setRangeInput((prev) => ({
                                  ...prev,
                                  nonWorkingItems: prev.nonWorkingItems?.map((item) =>
                                    item.nonWorkingId === code
                                      ? { ...item, nonWorkingHour: value }
                                      : item
                                  ),
                                  operatingHour: operatingHour,
                                  nonWorkingHour: nonWorkingHour,
                                  availabilityRate: availabilityRate,
                                  unavailabilityRate: unavailabilityRate
                                }));

                              },
                              errorsRangeInput.nonWorkingItems.isError,
                              () => setErrorsRangeInput(clearErrors()),
                            )}
                          </Box>
                        ))}
                      </SimpleGrid>
                    </Flex>
                    <Box>
                      <FormErrorMessage m={0}>{errorsRangeInput.nonWorkingItems.errorMessage}</FormErrorMessage>
                    </Box>
                  </FormControl>

                  <Flex alignItems="center" flexWrap="wrap" rowGap={2}>
                    <Flex alignItems="center" flexShrink={0}>
                      <Text whiteSpace="nowrap" ml={3} mr={1}>{t(`${NWH}.operatingTime`)}</Text>
                      <Input type="text" minW="90px" w="90px" p={2} background="#efefef" readOnly fontSize={20} value={rangeInput.operatingHour} />
                    </Flex>

                    <Flex alignItems="center" flexShrink={0}>
                      <Text whiteSpace="nowrap" ml={3} mr={1}>{t(`${NWH}.nonWorkingTimeLabel`)}</Text>
                      <Input type="text" minW="90px" w="90px" p={2} background="#efefef" readOnly fontSize={20} value={rangeInput.nonWorkingHour} />
                    </Flex>

                    <Flex alignItems="center" flexShrink={0}>
                      <Text whiteSpace="nowrap" ml={3} mr={1}>{t(`${NWH}.workTime`)}</Text>
                      <Input type="text" minW="90px" w="90px" p={2} background="#efefef" readOnly fontSize={20} value={rangeInput.workingHours} />
                    </Flex>

                    <Flex alignItems="center" flexShrink={0}>
                      <Text whiteSpace="nowrap" ml={3} mr={1}>{t(`${NWH}.operatingRate`)}</Text>
                      <Input type="text" minW="100px" w="100px" p={2} background="#efefef" readOnly fontSize={20} value={
                        (Number((rangeInput.availabilityRate || 0) * 100).toFixed(1) || "0") + " %"
                      } />
                    </Flex>

                    <Flex alignItems="center" flexShrink={0}>
                      <Text whiteSpace="nowrap" ml={3} mr={1}>{t(`${NWH}.nonOperatingRate`)}</Text>
                      <Input type="text" minW="100px" w="100px" p={2} background="#efefef" readOnly fontSize={20} value={
                        (Number((rangeInput.unavailabilityRate || 0) * 100).toFixed(1) || "0") + " %"
                      } />
                      <Box ml={2} display="flex" alignItems="center">
                        <HelpIcon>
                          <Text>{t(`${NWH}.help.rateNote`)}</Text>
                        </HelpIcon>
                      </Box>
                    </Flex>
                  </Flex>

                  <Box textAlign={"right"}>
                    {/* 一括入力ボタン */}
                    <Button
                      m={1}
                      colorScheme="blue"
                      onClick={() => {

                        // 4月始まりの年月を1月始まりの通常のYYYYMMに変換する関数
                        const fiscalYearToYearMonth = (fiscalYear: string | undefined, idx: number): number | undefined => {
                          const year = parseInt(fiscalYear?.match(/\d+/)?.[0] || "0", 10);
                          const month = idx < 9 ? idx + 4 : idx - 8;
                          const actualYear = month >= 4 ? year : year + 1;
                          return actualYear * 100 + month;
                        };

                        // 開始年月と終了年月を通常のYYYYMM形式に変換
                        const startYearMonthValue = fiscalYearToYearMonth(startYearMonth.year, startYearMonth.month - 4);
                        const endYearMonthValue = fiscalYearToYearMonth(endYearMonth.year, endYearMonth.month - 4);

                        // 開始年月と終了年月の必須チェック
                        if (startYearMonthValue === undefined || endYearMonthValue === undefined) {
                          // 開始年月または終了年月の変換に失敗した場合はスキップ
                          return;
                        }

                        // 全年度・全月次のデータを更新
                        props?.nonWorkingHoursData.forEach((yearData, yearIndex) => {
                          yearData.nonWorkingHoursByMonth.forEach((monthData, idx) => {

                            const currentYearMonth = fiscalYearToYearMonth(yearData.fiscalYear, idx);

                            if (!currentYearMonth) {
                              // カレント年月の変換に失敗した場合はスキップ
                              return;
                            }

                            if (currentYearMonth < startYearMonthValue || currentYearMonth > endYearMonthValue) {
                              // カレント年月が一括入力の範囲外の場合はスキップ
                              return;
                            }

                            // 入力チェック
                            if (!validOperatingHour(rangeInput.workTime, rangeInput.overTime, rangeInput.shiftSystem)) return;

                            // 元データのインデックスに戻す
                            const originalIndex = (idx + 3) % 12;

                            // 標準労働時間のstate更新
                            changeNonWorkingHoursData(yearData.fiscalYear, originalIndex, "workTime", rangeInput.workTime);

                            // 残業時間のstate更新
                            changeNonWorkingHoursData(yearData.fiscalYear, originalIndex, "overTime", rangeInput.overTime);

                            // 勤務体制のstate更新
                            changeNonWorkingHoursData(yearData.fiscalYear, originalIndex, "shiftSystem", rangeInput.shiftSystem);

                            // 労働時間のstate更新
                            handleCalcWorkingHour(yearData.fiscalYear, originalIndex, rangeInput.workTime, rangeInput.overTime, rangeInput.shiftSystem, true);

                            // JSONパース
                            const updatedNonWorkingItems = JSON.parse(
                              JSON.stringify(monthData.nonWorkingItems || [])
                            );

                            // 非稼働項目を更新
                            rangeInput.nonWorkingItems?.forEach(({ nonWorkingId, nonWorkingHour }) => {

                              // 該当の項目が既に存在するかチェック
                              const itemIndex = updatedNonWorkingItems.findIndex(
                                (item: { nonWorkingId: string }) => item.nonWorkingId === nonWorkingId
                              );

                              if (itemIndex !== -1) {
                                // 既存の項目を更新
                                updatedNonWorkingItems[itemIndex].nonWorkingHour = nonWorkingHour;
                              } else {
                                // 新しい項目を追加
                                updatedNonWorkingItems.push({
                                  nonWorkingId: nonWorkingId,
                                  nonWorkingHour: nonWorkingHour,
                                });
                              }

                              // 非稼働項目のstate更新
                              changeNonWorkingItems(
                                yearData.fiscalYear,
                                originalIndex,
                                nonWorkingId,
                                nonWorkingHour
                              );
                            });

                            // 稼働時間、非稼働時間のstate更新
                            handleCalcBasicHour(yearData.fiscalYear, originalIndex, updatedNonWorkingItems);
                          });
                        });
                      }}
                    >
                      {t(`${NWH}.bulkInput`)}
                    </Button>
                  </Box>

                </Stack>
              </AccordionPanel>
            </AccordionItem>
          </Accordion>

          {props?.nonWorkingHoursData?.map((yearData, index) => (
            <Accordion allowToggle key={index}>
              <AccordionItem>
                <h2>
                  <AccordionButton>
                    <Box flex="1" textAlign="left">
                      {fiscalYearsString[index]}
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                </h2>
                <AccordionPanel pb={4}>
                  {/* 残業有無（年度別・表示専用） */}
                  {/* 残業時間欄に値が入っているかどうかから算出する派生値のため、手動での変更はできない */}
                  {(() => {
                    // 4月～3月の順でインデックスを生成（配列は1月=0～12月=11）
                    const monthIndices = [3, 4, 5, 6, 7, 8, 9, 10, 11, 0, 1, 2];
                    const monthLabels = [4, 5, 6, 7, 8, 9, 10, 11, 12, 1, 2, 3].map(m => t('common.format.month', { month: m }));
                    return (
                      <Box mb={4} p={3} border="1px solid #e2e8f0" borderRadius="md" bg="gray.50">
                        <Text fontWeight="bold" mb={2}>{t(`${NWH}.overTimePresence`)}</Text>
                        <SimpleGrid columns={12} spacing={1}>
                          {monthIndices.map((monthIdx, displayIdx) => (
                            <Box key={displayIdx} textAlign="center">
                              <Text fontSize="xs" mb={1}>{monthLabels[displayIdx]}</Text>
                              <Switch
                                size="sm"
                                isDisabled
                                isChecked={
                                  !!yearData.nonWorkingHoursByMonth[monthIdx]?.overTime
                                  && yearData.nonWorkingHoursByMonth[monthIdx].overTime !== DEFAULT_TIME
                                }
                              />
                            </Box>
                          ))}
                        </SimpleGrid>
                      </Box>
                    );
                  })()}

                  <Tabs variant="enclosed" isLazy>
                    <TabList>
                      {[
                        ...yearData.nonWorkingHoursByMonth.slice(3, 12),
                        ...yearData.nonWorkingHoursByMonth.slice(0, 3),
                      ].map((_, idx) => (
                        <Tab key={idx}>{t('common.format.month', { month: idx + 4 > 12 ? idx - 8 : idx + 4 })}</Tab>
                      ))}
                    </TabList>
                    <TabPanels>
                      {[
                        ...yearData.nonWorkingHoursByMonth.slice(3, 12),
                        ...yearData.nonWorkingHoursByMonth.slice(0, 3),
                      ].map((monthData, idx) => (
                        <TabPanel key={idx}>
                          <Stack spacing={1}>
                            <HStack spacing={0} align="start" minH={12}>
                              <FormControl w="fit-content" minW="220px" isInvalid={errors.workTime.isError}>
                                <Flex align="center">
                                  <Text minW={LABEL_WIDTH} m={0} mr={2} whiteSpace="nowrap">{t(`${NWH}.standardWorkTime`)}</Text>
                                  <CeasTimePicker
                                    isInvalid={errors.workTime.isError}
                                    value={monthData.workTime || DEFAULT_TIME}
                                    onChange={(value) => {

                                      // 入力チェック
                                      if (!validOperatingHour(value, monthData.overTime, monthData.shiftSystem)) {
                                        return;
                                      }

                                      // 元データのインデックスに戻す
                                      const originalIndex = (idx + 3) % 12;

                                      // state更新
                                      changeNonWorkingHoursData(yearData.fiscalYear, originalIndex, "workTime", value);

                                      // 労働時間を算出
                                      handleCalcWorkingHour(yearData.fiscalYear, originalIndex, value, monthData.overTime, monthData.shiftSystem, true);
                                    }}
                                    onBlur={() => {
                                      setErrors(clearErrors());
                                    }}
                                  />
                                </Flex>
                                <FormErrorMessage>{errors.workTime.errorMessage}</FormErrorMessage>
                              </FormControl>

                              <FormControl ml={6} w="185px" isInvalid={errors.overTime.isError}>
                                <Flex align="center">
                                  <Text w={75} m={0} whiteSpace="nowrap">{t(`${NWH}.overTime`)}</Text>
                                  <CeasTimePicker
                                    isInvalid={errors.overTime.isError}
                                    value={monthData.overTime || DEFAULT_TIME}
                                    onChange={(value) => {

                                      // 入力チェック
                                      if (!validOperatingHour(monthData.workTime, value, monthData.shiftSystem)) {
                                        return;
                                      }

                                      // 元データのインデックスに戻す
                                      const originalIndex = (idx + 3) % 12;

                                      // state更新
                                      changeNonWorkingHoursData(yearData.fiscalYear, originalIndex, "overTime", value);

                                      // 労働時間を算出
                                      handleCalcWorkingHour(yearData.fiscalYear, originalIndex, monthData.workTime, value, monthData.shiftSystem, true);
                                    }}
                                    onBlur={() => {
                                      setErrors(clearErrors());
                                    }}
                                  />
                                </Flex>
                                <FormErrorMessage>{errors.overTime.errorMessage}</FormErrorMessage>
                              </FormControl>
                              <FormControl ml={6} w="220px" isInvalid={errors.shiftSystem.isError}>
                                <Flex align="center">
                                  <Text minW={90} m={0} mr={2} whiteSpace="nowrap">{t(`${NWH}.shiftSystem`)}</Text>
                                  <Select w="130px"
                                    value={monthData.shiftSystem}
                                    onChange={(e) => {

                                      // 入力チェック
                                      if (!validOperatingHour(monthData.workTime, monthData.overTime, e.target.value)) {
                                        return;
                                      }

                                      // 元データのインデックスに戻す
                                      const originalIndex = (idx + 3) % 12;

                                      // state更新
                                      changeNonWorkingHoursData(yearData.fiscalYear, originalIndex, "shiftSystem", e.currentTarget.value);

                                      // 労働時間を算出
                                      handleCalcWorkingHour(yearData.fiscalYear, originalIndex, monthData.workTime, monthData.overTime, e.currentTarget.value, true);
                                    }}
                                    onBlur={() => {
                                      setErrors(clearErrors());
                                    }}
                                  >
                                    {Object.entries(codeToNameMap(ShiftSystem)).map(([code, name]) => (
                                      <option key={code} value={code}>
                                        {name}
                                      </option>
                                    ))}
                                  </Select>
                                </Flex>
                                <FormErrorMessage>{errors.shiftSystem.errorMessage}</FormErrorMessage>
                              </FormControl>
                            </HStack>



                            <FormControl isInvalid={errors.nonWorkingItems.isError}>
                              <Flex align="start">
                                <Text minW={LABEL_WIDTH} m={0} mt={3} whiteSpace="nowrap">
                                  {t(`${NWH}.nonWorkingItems`)}
                                </Text>
                                <SimpleGrid columns={2} spacing={4} flex={1}>
                                  {nonWorkingItemsColumns.map((columnItems, columnIndex) => (
                                    <Box key={columnIndex}>
                                      {renderNonWorkingItemsColumn(
                                        columnItems,
                                        (code) => monthData.nonWorkingItems?.find(
                                          (item) => item.nonWorkingId === code
                                        )?.nonWorkingHour || DEFAULT_TIME,
                                        (code, value) => {

                                          // 現在の入力値を仮反映（項目が欠けている既存データでも
                                          // 値が反映されるよう、正規の項目一覧から再構築する）
                                          const nonWorkingItems: NonWorkingType[] = buildNonWorkingItems(
                                            monthData.nonWorkingItems,
                                            { id: code, value }
                                          );

                                          // 入力チェック
                                          if (!validNonWorkingHours(nonWorkingItems)) {
                                            return;
                                          }

                                          // 元データのインデックスに戻す
                                          const originalIndex = (idx + 3) % 12;

                                          // state更新
                                          changeNonWorkingItems(yearData.fiscalYear, originalIndex, code, value);

                                          // 稼働時間、稼働率、非稼働時間、非稼働率を算出
                                          handleCalcBasicHour(yearData.fiscalYear, originalIndex, nonWorkingItems);
                                        },
                                        errors.nonWorkingItems.isError,
                                        () => setErrors(clearErrors()),
                                      )}
                                    </Box>
                                  ))}
                                </SimpleGrid>
                              </Flex>
                              <Box>
                                <FormErrorMessage m={0}>{errors.nonWorkingItems.errorMessage}</FormErrorMessage>
                              </Box>
                            </FormControl>

                            <Flex alignItems="center" flexWrap="wrap" rowGap={2}>
                              <Flex alignItems="center" flexShrink={0}>
                                <Text whiteSpace="nowrap" ml={3} mr={1}>{t(`${NWH}.operatingTime`)}</Text>
                                <Input type="text" minW="90px" w="90px" p={2} background="#efefef" readOnly fontSize={20} value={monthData.operatingHour || DEFAULT_TIME} />
                              </Flex>

                              <Flex alignItems="center" flexShrink={0}>
                                <Text whiteSpace="nowrap" ml={3} mr={1}>{t(`${NWH}.nonWorkingTimeLabel`)}</Text>
                                <Input type="text" minW="90px" w="90px" p={2} background="#efefef" readOnly fontSize={20} value={monthData.nonWorkingHour || DEFAULT_TIME} />
                              </Flex>

                              <Flex alignItems="center" flexShrink={0}>
                                <Text whiteSpace="nowrap" ml={3} mr={1}>{t(`${NWH}.workTime`)}</Text>
                                <Input type="text" minW="90px" w="90px" p={2} background="#efefef" readOnly fontSize={20} value={monthData.workingHours || DEFAULT_TIME} />
                              </Flex>

                              <Flex alignItems="center" flexShrink={0}>
                                <Text whiteSpace="nowrap" ml={3} mr={1}>{t(`${NWH}.operatingRate`)}</Text>
                                <Input type="text" minW="100px" w="100px" p={2} background="#efefef" readOnly fontSize={20} value={
                                  (Number((monthData.availabilityRate || 0) * 100).toFixed(1) || "0") + " %"
                                } />
                              </Flex>

                              <Flex alignItems="center" flexShrink={0}>
                                <Text whiteSpace="nowrap" ml={3} mr={1}>{t(`${NWH}.nonOperatingRate`)}</Text>
                                <Input type="text" minW="100px" w="100px" p={2} background="#efefef" readOnly fontSize={20} value={
                                  (Number((monthData.unavailabilityRate || 0) * 100).toFixed(1) || "0") + " %"
                                } />
                              </Flex>
                            </Flex>

                          </Stack >
                        </TabPanel>
                      ))}
                    </TabPanels>
                  </Tabs>

                </AccordionPanel>
              </AccordionItem>
            </Accordion>
          ))}

        </Stack>
      </Box>

    </>
  )
});

export default NonWorkingHours;
